import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { getApiErrorMessage, resetPassword } from '../api/api';
import '../styles/global.css';


export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get('token') || '').trim();
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('O link de recuperação está incompleto. Solicite um novo link.');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('As senhas informadas não são iguais.');
      return;
    }

    setLoading(true);
    try {
      const result = await resetPassword(token, password);
      setSuccess(result?.detail || 'Senha alterada com sucesso. Faça login novamente.');
      setPassword('');
      setPasswordConfirmation('');
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          'Não foi possível redefinir a senha. Solicite um novo link e tente novamente.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const passwordType = showPassword ? 'text' : 'password';

  return (
    <div className="login-page">
      <form className="login-form" onSubmit={handleSubmit}>
        <img src="/logo.png" alt="Logo BlueZap" className="logo-imagem" />
        <h2>Criar nova senha</h2>
        <p className="auth-form-intro">
          Sua conta foi identificada pelo link. Informe apenas a nova senha e sua confirmação.
          Use pelo menos 8 caracteres, incluindo letra maiúscula, minúscula e número.
        </p>

        <div className="password-field">
          <input
            id="reset-password"
            name="password"
            type={passwordType}
            placeholder="Nova senha"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={8}
            maxLength={128}
            required
            disabled={loading || Boolean(success)}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword((current) => !current)}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {showPassword ? (
              <Eye className="password-toggle__icon" aria-hidden="true" />
            ) : (
              <EyeOff className="password-toggle__icon" aria-hidden="true" />
            )}
          </button>
        </div>

        <div className="password-field">
          <input
            id="reset-password-confirmation"
            name="password_confirmation"
            type={passwordType}
            placeholder="Confirme a nova senha"
            value={passwordConfirmation}
            onChange={(event) => setPasswordConfirmation(event.target.value)}
            autoComplete="new-password"
            minLength={8}
            maxLength={128}
            required
            disabled={loading || Boolean(success)}
          />
        </div>

        <button
          className="auth-form-submit"
          type="submit"
          disabled={loading || Boolean(success) || !token}
        >
          {loading ? 'Alterando...' : 'Alterar senha'}
        </button>

        {!token ? (
          <p className="auth-form-message auth-form-message--error" role="alert">
            O link de recuperação está incompleto. Solicite um novo link.
          </p>
        ) : null}
        {success ? (
          <p className="auth-form-message auth-form-message--success" role="status">
            {success}
          </p>
        ) : null}
        {error ? (
          <p className="auth-form-message auth-form-message--error" role="alert">
            {error}
          </p>
        ) : null}

        <Link className="auth-form-link" to={success ? '/login' : '/forgot-password'}>
          {success ? 'Ir para o login' : 'Solicitar um novo link'}
        </Link>
      </form>
    </div>
  );
}
